/**
 * useGrouping Hook
 * Merges multiple elements into a single group element.
 * Original elements are deleted; their data is stored for ungrouping.
 * The group element behaves as a normal single element (move, select, etc).
 */

import { useCallback } from 'react'
import { dia, shapes } from '@joint/core'
import {
  currentOriginals,
  frameAngle,
  frameVertices,
  isLightGeometry,
  snapshotVertices,
  writePolygon,
} from '../lib/lightFrameGeometry'

interface GroupOptions {
  groupName?: string
  objectName?: string
  typeId?: string | null
  typeColor?: string | null
}

interface UseGroupingReturn {
  groupElements: (elementIds: string[], options?: GroupOptions) => string | null
  addToGroup: (groupId: string, elementIds: string[]) => void
  /** 그룹 해제. 복원된 primitive element 들의 새 id 배열을 반환. 그룹이 없거나 실패 시 빈 배열. */
  ungroupElements: (groupId: string) => string[]
  isGroupElement: (elementId: string) => boolean
  canGroup: (elementIds: string[]) => boolean
  getGroups: () => { id: string; name: string }[]
}

export function useGrouping(graph: dia.Graph | null): UseGroupingReturn {
  const canGroup = useCallback((elementIds: string[]): boolean => {
    if (!graph || elementIds.length < 2) return false
    // Allow both regular elements AND existing groups to be selected for grouping
    const valid = elementIds.filter(id => {
      const cell = graph.getCell(id)
      return cell?.isElement()
    })
    return valid.length >= 2
  }, [graph])

  const groupElements = useCallback((elementIds: string[], options?: GroupOptions): string | null => {
    if (!graph || elementIds.length < 2) return null

    // Include all elements (regular + existing groups)
    const elements = elementIds
      .map(id => graph.getCell(id))
      .filter((el): el is dia.Element => !!el && el.isElement())

    if (elements.length < 2) return null

    const originals = elements.map((el) => {
      const snapshot = JSON.parse(JSON.stringify(el.toJSON()))
      delete snapshot.id
      return snapshot
    })
    const groupName = options?.groupName || `Group ${elements.length}`
    const oriented = ['l-light', 'l-lightingline', 'c-light'].includes(groupName)
    const groupVertices = frameVertices(originals.flatMap(snapshotVertices), oriented)

    // Remove original elements
    for (const el of elements) {
      el.remove()
    }

    // Stroke color from ObjectType, fill always transparent
    const strokeColor = options?.typeColor && options.typeColor.startsWith('#')
      ? options.typeColor
      : '#3B82F6'

    // Create single merged element — stroke only, no fill, no label
    const merged = new shapes.standard.Path({
      attrs: {
        body: {
          fill: 'transparent',
          stroke: strokeColor,
          strokeWidth: 2,
        },
        label: { text: '' },
      },
    })

    // Set objectTypeId for type mapping compatibility
    if (options?.typeId) {
      merged.prop('objectTypeId', options.typeId)
    }

    const objName = options?.objectName || groupName

    merged.set('data', {
      isGroup: true,
      type: 'polygon',
      layer: groupName,
      objectType: groupName,
      typeId: options?.typeId || null,
      groupName,
      geometryAngle: frameAngle(groupVertices),
      properties: {
        name: objName,
      },
      originalElements: originals,
    })

    writePolygon(merged, groupVertices)
    graph.addCell(merged)
    return merged.id as string
  }, [graph])

  const ungroupElements = useCallback((groupId: string): string[] => {
    if (!graph) return []
    const cell = graph.getCell(groupId)
    if (!cell?.isElement()) return []

    const data = cell.get('data')
    if (!data?.isGroup || !data.originalElements) return []

    const originals = currentOriginals(cell)
    const restoredIds: string[] = []
    for (const original of originals) {
      const before = new Set(graph.getElements().map((el) => el.id))
      graph.addCell(original)
      const restored = graph.getElements().find((el) => !before.has(el.id))
      if (restored) restoredIds.push(restored.id as string)
    }

    cell.remove()
    return restoredIds
  }, [graph])

  const addToGroup = useCallback((groupId: string, elementIds: string[]): void => {
    if (!graph) return
    const groupCell = graph.getCell(groupId)
    if (!groupCell?.isElement() || !groupCell.get('data')?.isGroup) return

    const elements = elementIds
      .map(id => graph.getCell(id))
      .filter((el): el is dia.Element => !!el && el.isElement() && el.id !== groupId)

    if (elements.length === 0) return

    const data = groupCell.get('data')
    const allOriginals = [
      ...currentOriginals(groupCell),
      ...elements.map((el) => {
        const snapshot = JSON.parse(JSON.stringify(el.toJSON()))
        delete snapshot.id
        return snapshot
      }),
    ]
    const points = allOriginals.flatMap(snapshotVertices)
    const vertices = frameVertices(points, isLightGeometry(groupCell))
    for (const el of elements) el.remove()
    writePolygon(groupCell, vertices)
    groupCell.set('data', {
      ...data,
      ...groupCell.get('data'),
      geometryAngle: frameAngle(vertices),
      originalElements: allOriginals,
    })
  }, [graph])

  const isGroupElement = useCallback((elementId: string): boolean => {
    if (!graph) return false
    const cell = graph.getCell(elementId)
    return !!cell?.isElement() && cell.get('data')?.isGroup === true
  }, [graph])

  const getGroups = useCallback((): { id: string; name: string }[] => {
    if (!graph) return []
    return graph.getElements()
      .filter(el => el.get('data')?.isGroup)
      .map(el => ({
        id: el.id as string,
        name: el.get('data')?.groupName || el.get('data')?.properties?.name || 'Group'
      }))
  }, [graph])

  return { groupElements, addToGroup, ungroupElements, isGroupElement, canGroup, getGroups }
}
